import unfetch from 'isomorphic-unfetch';
import { NIL as EMPTY_GUID } from 'uuid';
import { HttpNetworkError, HttpStatusError } from './apiFetchErrors';
import { FetchResponse } from './fetchResponse';
import {
  getModifiedUrl,
  IFetchOptions,
  TMethod,
  tryAppendRequestContentTypeHeader
} from './fetchUtilities';

const DEFAULT_OPTIONS: IFetchOptions = {
  body: null,
  dataPoint: '',
  queryParams: {},
  method: 'GET',
  headers: {},
  token: ''
};

const getErrorReturnObject = <T>(msg: string): FetchResponse<T> => [
  null as any,
  new Error(msg),
  null,
  undefined
];

const ERROR_CHECK = ['undefined', EMPTY_GUID];

// Main wrapper function
const fetch = async <T>(
  url: string,
  options?: Partial<IFetchOptions>
): Promise<FetchResponse<T>> => {
  // we are checking for something like https://production.api.maxpreps.internal/leagues/undefined/standings/v1
  if (typeof url !== 'string') {
    return getErrorReturnObject(`url must be of type 'string'`);
  }

  var invalid = ERROR_CHECK.find(str => url.indexOf(str) > -1);
  // https://production.api.maxpreps.internal/leagues/00000000-0000-0000-0000-000000000000/standings/v1
  if (invalid) {
    return getErrorReturnObject(`${invalid} found in url: ${url}`);
  }

  const composedOptions = { ...DEFAULT_OPTIONS, ...options };

  let response: Response | undefined;

  // everything before unfetch is potentially an error we aren't accounting for correctly.
  // if we are getting weird NetworkError behavior wrap this all in a try/catch and throw
  // a custom error that can be managed at the simple wrapper layer
  const modifiedUrl = getModifiedUrl(url, composedOptions);

  // set the header token if one was provided
  if (composedOptions?.token) {
    composedOptions.headers = {
      ...composedOptions.headers,
      'X-MP-UserToken': composedOptions.token
    };
  }

  tryAppendRequestContentTypeHeader(composedOptions);

  // if (env !== 'production')
  // {
  // console.log('API Fetch - ModifiedUrl', modifiedUrl.href);
  // }

  try {
    response = await unfetch(
      new Request(modifiedUrl.toString(), composedOptions as any)
    );
  } catch (_error) {
    let error: Error;
    if (_error instanceof Error) {
      error = new HttpNetworkError(_error);
    } else {
      error = new Error(
        typeof _error === 'string' ? _error : 'Unknown network error occurred'
      );
    }

    // technically _error can end up being a non-error object.
    // if fetch method has some logic wrapping unfetch this could happen (or if unfetch has some obsurce error but i doubt that'd be the case)
    return [null as never, error, null, response];
  }

  let json: T | null = null;
  let jsonParseError: Error | undefined;
  try {
    json = await response.json();
  } catch (_error) {
    if (_error instanceof Error) {
      jsonParseError = new HttpNetworkError(_error);
    } else {
      jsonParseError = new Error(
        typeof _error === 'string'
          ? _error
          : 'Unknown json parse error occurred'
      );
    }
  }

  if (!response.ok) {
    return [null, new HttpStatusError(response, json), json, response];
  }

  // we have a valid response, lets try to parse the response body
  if (jsonParseError) {
    return [null, jsonParseError as Error, null, response];
  }

  const data =
    composedOptions.dataPoint === 'NoDataPoint'
      ? json
      : json?.[
          composedOptions?.dataPoint ? composedOptions?.dataPoint : 'data'
        ];

  // We should probably create a copy of the 'data' object so any manipulations don't also affect the json object.
  // If the current implementation ever becomes a probably please implement above!
  return [data, undefined, json, response];
};

// Legacy wrappers - not preferred

export const webFormsSimpleWrapper = async (
  url: string,
  options?: Partial<IFetchOptions>
) => fetch(url, { ...options, wfRequest: true });

export const ghostApiSimpleWrapper = async (
  url: string,
  options?: Partial<IFetchOptions>
) => fetch(url, options);

type ComplexFetchAsyncParameters = [
  url: string,
  body: any,
  token: string,
  headers?: object | null
];

const FetchAsync = async <T>(
  method: TMethod,
  url: string,
  body: any,
  token: string,
  headers?: object | null
): Promise<FetchResponse<T>> =>
  fetch<T>(url, {
    ...DEFAULT_OPTIONS,
    method,
    headers: (headers ?? undefined) as Record<string, string>,
    token,
    body: body ? JSON.stringify(body) : undefined
  });

// API Service Client wrappers - preferred
export const GetAsync = async <T>(
  url: string,
  token: string,
  headers?: object | null
): Promise<FetchResponse<T>> => FetchAsync('GET', url, null, token, headers);

export const PostAsync = async <T>(
  ...args: ComplexFetchAsyncParameters
): Promise<FetchResponse<T>> => FetchAsync('POST', ...args);

export const PutAsync = async <T>(
  ...args: ComplexFetchAsyncParameters
): Promise<FetchResponse<T>> => FetchAsync('PUT', ...args);

export const PatchAsync = async <T>(
  ...args: ComplexFetchAsyncParameters
): Promise<FetchResponse<T>> => FetchAsync('PATCH', ...args);

export const DeleteAsync = async <T>(
  url: string,
  token: string,
  headers?: object | null
): Promise<FetchResponse<T>> => FetchAsync('DELETE', url, null, token, headers);
